import React, { useEffect, useState } from "react"
import { useShopify } from "../ShopifyProvider"
import { useQuery } from "react-query"
import {
  Box,
  BoxProps,
  Stack,
  StackProps,
  useMediaQuery,
} from "@chakra-ui/react"
import { Link } from "../Link"
import { matchPath, useLocation, useMatch } from "react-router"
import { useCart } from "../ShopifyProvider/CartProvider"
import { chakra } from "@chakra-ui/system"

const isTouch = "ontouchstart" in window

type MenuItemProps = BoxProps & {
  expanded?: boolean
  active?: boolean
  url?: string
}
const MenuItem = ({
  expanded = true,
  active = false,
  url,
  children,
  ...props
}: MenuItemProps) => {
  const [animateIn, setAnimateIn] = useState(false)
  useEffect(() => {
    setTimeout(() => setAnimateIn(true), 100)
  }, [])
  const showHighlight = useMatch("/products/:uid") !== null
  const backgroundColor = showHighlight && active ? "background" : "transparent"
  return (
    <Box
      height={expanded && animateIn ? 4 : 0}
      transition="height 0.5s"
      overflow="hidden"
      textTransform="uppercase"
      display="inline-block"
      userSelect="none"
      {...props}
    >
      {url ? (
        <Link backgroundColor={backgroundColor} to={url}>
          {children}
        </Link>
      ) : (
        <chakra.span backgroundColor={backgroundColor}>{children}</chakra.span>
      )}
    </Box>
  )
}

const Indent = (props: StackProps) => (
  <Stack spacing={0} paddingLeft={[2, null, 4]} {...props} />
)

export const Menu = () => {
  const sdk = useShopify()
  const location = useLocation()
  const matchesCurrentPath = (path: string) =>
    matchPath({ path, end: false }, location.pathname) !== null

  const [isMobile] = useMediaQuery("(max-width: 768px)")
  const isProductRoute = matchesCurrentPath("/products")
  const [mouseOver, setMouseOver] = useState(false)

  const [isInit, setIsInit] = useState(isProductRoute)
  useEffect(() => {
    const timer = setTimeout(() => setIsInit(false), 2000)
    return () => clearTimeout(timer)
  }, [isInit])

  const [productListToggled, setProductListToggled] = useState(isProductRoute)

  const expandProductList =
    (!isMobile && isProductRoute) || productListToggled || mouseOver || isInit

  useEffect(() => {
    if (!matchPath({ path: "/products" }, location.pathname))
      setProductListToggled(false)
  }, [location.pathname])

  const { data } = useQuery("menu", () => sdk.menu())
  const cart = useCart()
  const cartItems = cart?.lines.edges.reduce(
    (total, { node: lineItem }) => total + lineItem.quantity,
    0
  )
  if (!data) return null
  return (
    <Stack
      position="fixed"
      top="pageMargin"
      left="pageMargin"
      marginTop="-0.2em"
      zIndex={20}
      spacing={0}
      onMouseEnter={!isTouch ? () => setMouseOver(true) : undefined}
      onMouseLeave={!isTouch ? () => setMouseOver(false) : undefined}
      userSelect="none"
    >
      <MenuItem
        onClick={() => {
          const el = document.getElementById("content-root")
          el?.scrollIntoView({
            behavior: "smooth",
            block: "start",
          })
        }}
      >
        {process.env.REACT_APP_SITE_URL!}
      </MenuItem>
      <Indent>
        <MenuItem onClick={() => setProductListToggled(!productListToggled)}>
          /products
          <chakra.span
            transition="opacity 0.5s"
            opacity={expandProductList ? 0 : 1}
          >
            ...
          </chakra.span>
        </MenuItem>
        <Indent>
          {data.menu?.products.edges
            .filter(({ node: product }) => !product.tags.includes("draft"))
            .map(({ node: product }) => {
              const url = `/products/${product.handle}`
              const isCurrentPath = matchesCurrentPath(url)
              return (
                <MenuItem
                  url={url}
                  active={isCurrentPath}
                  expanded={expandProductList || isCurrentPath}
                  key={product.handle}
                >
                  /{product.title.replaceAll(" ", "-")}
                </MenuItem>
              )
            })}
        </Indent>
        <MenuItem
          url="/information"
          active={matchesCurrentPath("/information")}
        >
          /information
        </MenuItem>
        <MenuItem url="/cart" active={matchesCurrentPath("/cart")}>
          /cart {cartItems !== undefined ? `[${cartItems}]` : ""}
        </MenuItem>
      </Indent>
    </Stack>
  )
}
